import React, { forwardRef } from 'react'
import { useTranslation } from 'react-i18next'
import Cascader, { CascaderAutoProps, DefaultOptionType } from 'antd/es/cascader'
import { GetRef } from 'antd/es'
import { useScenarios } from '../../queries/useScenarios'
import { defaultOptionsSelector } from '../../selectors/selectors'
import { BudgetingScenario } from '../../types/BudgetingScenario'
import { ReportDataType } from '../../../../redux/context/reports/types'

type ScenarioCascaderProps = CascaderAutoProps & {
  companyId?: string
  optionSelector?: (data: BudgetingScenario[]) => DefaultOptionType[]
}

type CascaderRefType = GetRef<typeof Cascader>

const ScenarioCascader = forwardRef<CascaderRefType, ScenarioCascaderProps>(
  ({ companyId, optionSelector, ...props }, ref) => {
    const { t } = useTranslation()
    const {
      data: budgetOptions,
      isPending: scenariosPending,
      isLoading: scenariosLoading
    } = useScenarios({
      companyId,
      select: optionSelector || defaultOptionsSelector,
      dataType: ReportDataType.budget
    })
    const {
      data: forecastOptions,
      isPending: forecastsPending,
      isLoading: forecastsLoading
    } = useScenarios({
      companyId,
      select: optionSelector || defaultOptionsSelector,
      dataType: ReportDataType.forecast
    })

    const options: DefaultOptionType[] = [
      {
        value: ReportDataType.actuals,
        label: t('global:actuals')
      },
      {
        value: ReportDataType.budget,
        label: t('global:budget'),
        disabled: budgetOptions.length < 1,
        children: budgetOptions
      },
      {
        value: ReportDataType.forecast,
        label: t('global:forecast'),
        disabled: forecastOptions.length < 1,
        children: forecastOptions
      }
    ]

    return (
      <Cascader
        ref={ref}
        disabled={props.disabled ?? (!companyId || scenariosPending || forecastsPending)}
        options={options}
        loading={scenariosLoading || forecastsLoading}
        displayRender={(label, selectedOptions) => {
          if (selectedOptions?.[0]?.value === ReportDataType.actuals) {
            return label[0]
          }
          return label.join(' / ')
        }}
        {...props}
      />
    )
  }
)

export default ScenarioCascader
